import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, CurrencyPipe, DatePipe, DecimalPipe, PercentPipe, TitleCasePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule, NgZone, inject, provideAppInitializer } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_SELECT_CONFIG, MatSelectModule } from '@angular/material/select';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideCollectionsWorkflowTracking } from '@aiwyn/common/collections';
import { provideInvoicesWorkflowTracking } from '@aiwyn/common/invoices';
import { providePaymentsWorkflowTracking } from '@aiwyn/common/payments';
import { provideWorkPoolsWorkflowTracking } from '@aiwyn/common/work-pools';
import { provideEmailDeliveryWorkflowTracking } from '@aiwyn/email-delivery-hub';
import { EDITOR_STAFF_LOADER } from '@aiwyn/foundations/components/editor';
import { ApiModule, StaffApiService, UploadApiService } from '@anduin/api';
import {
  AuthErrorInterceptor,
  AuthInterceptor,
  AuthModule,
  ExtendedApolloService,
  LaunchDarklyFlag,
  LaunchDarklyService,
  PENDO_TRACKER,
  TokenService,
  provideAuthWorkflowTracking,
} from '@anduin/auth';
import {
  ConfigModule,
  ConfigService,
  Globals,
  InitService,
  LOG_ROCKET_TRACKER,
  LogRocketLogger,
  provideSessionReplayCorrelation,
} from '@anduin/config';
import {
  APP_VERSION_TOKEN,
  AbstractUploadApiService,
  CURRENCY_CODE_PROVIDER,
  CURRENCY_FORMAT_PROVIDER,
  ChunkLoadErrorMonitorService,
  DEFAULT_DATE_FORMAT_PROVIDER,
  ExtendedCurrencyPipe,
  ExtendedDatePipe,
  ExtendedPercentPipe,
  GenericErrorComponent,
  Logger,
  OrdinalPipe,
  RETRY_UNCHECKED_INTERCEPTOR_PROVIDER,
  SPLIT_LIST_DMS_FOLDER_ITEMS_FLAG,
  SharedModule,
  SharedState,
  provideTracking,
} from '@anduin/shared';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { QueryClient, provideTanStackQuery, withDevtools } from '@tanstack/angular-query-experimental';
import { AgGridModule } from 'ag-grid-angular';
import { OAuthModule } from 'angular-oauth2-oidc';
import { Apollo } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { DpDatePickerModule } from 'ng2-date-picker';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ToolbarComponent } from 'ngx-dev-toolbar';
import { IConfig, NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NgxSpinnerModule } from 'ngx-spinner';
import packageJson from '../../package.json';
import { ENVIRONMENT_PROVIDER } from '../environments';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ClientsTableComponent } from './components/clients/clients-table/clients-table.component';
import { ClientsComponent } from './components/clients/clients.component';
import { HeaderComponent } from './components/header/header.component';
import { LoginComponent } from './components/login/login.component';
import { LogoutComponent } from './components/logout/logout.component';
import { SidenavLinkOptionComponent } from './components/sidenav/sidenav-link-option/sidenav-link-option.component';
import { SidenavMenuOptionComponent } from './components/sidenav/sidenav-menu-option/sidenav-menu-option.component';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { ApiBaseUrlInterceptor } from './interceptors/api-base-url.interceptor';
import { ChunkErrorDialogComponent } from './interceptors/chunk-error-dialog/chunk-error-dialog.component';
import { GlobalErrorHandlerService } from './interceptors/global-error-handler.service';
import { HttpErrorInterceptor } from './interceptors/http-error-interceptor.service';
import { XhrInterceptor } from './interceptors/xhr.interceptor';
import { AppState } from './state/app.state';

/**
 * This function returns the server url wired into argo via a property file before starting the application so
 * that the API calls can use it to communicate with the server. It also returns whether or not we are using
 * SSO.
 */
export function initialize(initService: InitService) {
  return (): Promise<any> => {
    return initService.initializeApplication();
  };
}

/**
 * This imports the default configuration for ngx-mask used to mask the time input value in global settings
 */
export const options: Partial<IConfig> | (() => Partial<IConfig>) = null;

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    SidenavComponent,
    SidenavLinkOptionComponent,
    SidenavMenuOptionComponent,
    LoginComponent,
    GenericErrorComponent,
    ClientsComponent,
    ClientsTableComponent,
    LogoutComponent,
    ChunkErrorDialogComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    NgxsStoragePluginModule.forRoot({ keys: [SharedState] }),
    NgxSpinnerModule,
    BrowserModule,
    CommonModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatInputModule,
    MatAutocompleteModule,
    MatMenuModule,
    MatTableModule,
    MatIconModule,
    MatSortModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatListModule,
    MatSelectModule,
    MatTooltipModule,
    PdfViewerModule,
    OAuthModule.forRoot({
      resourceServer: {
        allowedUrls: ['/api', 'http'],
        sendAccessToken: true,
      },
    }),
    MatOptionModule,
    MatTabsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatSortModule,
    DragDropModule,
    DpDatePickerModule,
    NgxsModule.forRoot([SharedState, AppState], {
      developmentMode: !environment.production,
      // Selectors otherwise compile their prop getters with `new Function(...)`,
      // which trips script-src 'unsafe-eval'.
      compatibility: { strictContentSecurityPolicy: true },
    }),
    NgxsReduxDevtoolsPluginModule.forRoot({
      name: 'firm-portal',
      disabled: environment.production,
    }),
    SharedModule,
    ConfigModule,
    AuthModule,
    ApiModule,
    AgGridModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ToolbarComponent,
  ],
  providers: [
    provideTanStackQuery(
      new QueryClient(),
      withDevtools(() => ({ loadDevtools: !!environment?.enableQueryDevtools })),
    ),
    { provide: Logger, useClass: LogRocketLogger },
    provideTracking({ enable: [PENDO_TRACKER, LOG_ROCKET_TRACKER] }),
    provideSessionReplayCorrelation(),
    provideAuthWorkflowTracking(),
    provideCollectionsWorkflowTracking(),
    provideEmailDeliveryWorkflowTracking(),
    provideInvoicesWorkflowTracking(),
    providePaymentsWorkflowTracking(),
    provideWorkPoolsWorkflowTracking(),
    {
      provide: DatePipe,
      useClass: ExtendedDatePipe,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandlerService,
    },
    {
      // Prepend base URL to /api requests (runs first so auth interceptors see full URL)
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseUrlInterceptor,
      multi: true,
    },
    {
      // Intercept Auth calls
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      // Add an interceptor to catch 401 errors and attempt to reauthenticate
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true,
    },
    // StorageService,
    {
      // Replace the header in API calls to avoid 401 errors
      provide: HTTP_INTERCEPTORS,
      useClass: XhrInterceptor,
      multi: true,
    },
    {
      // Intercept API calls and call redirect to the login page if an error occurs
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      // Use custom percent pipe by default when injecting it as a dependency
      provide: PercentPipe,
      useClass: ExtendedPercentPipe,
    },
    {
      provide: AbstractUploadApiService,
      useClass: UploadApiService,
    },
    {
      provide: EDITOR_STAFF_LOADER,
      useFactory: (staffApi: StaffApiService) => (userId: number) => staffApi.getStaffById(userId),
      deps: [StaffApiService],
    },
    TitleCasePipe,
    DecimalPipe,
    CurrencyPipe,
    ExtendedCurrencyPipe,
    OrdinalPipe,
    // Retry 500 and 0 errors
    RETRY_UNCHECKED_INTERCEPTOR_PROVIDER,
    DEFAULT_DATE_FORMAT_PROVIDER,
    provideAppInitializer(() => {
      const initializerFn = initialize(inject(InitService));
      return initializerFn();
    }),
    ENVIRONMENT_PROVIDER,
    CURRENCY_FORMAT_PROVIDER,
    CURRENCY_CODE_PROVIDER,
    // App version provider
    {
      provide: APP_VERSION_TOKEN,
      useValue: packageJson.version,
    },
    {
      provide: SPLIT_LIST_DMS_FOLDER_ITEMS_FLAG,
      useFactory: () => inject(LaunchDarklyService).getFeatureFlagSignal(LaunchDarklyFlag.SPLIT_LIST_DMS_FOLDER_ITEMS_ENDPOINT),
    },
    provideNgxMask(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideAuth(() => getAuth()),
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { enterAnimationDuration: 0 },
    },
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: ['mat-select-panel'] },
    },
    {
      provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        hideSingleSelectionIndicator: true,
        appearance: 'legacy',
        disableInteractive: true,
      },
    },
    {
      provide: Apollo,
      useClass: ExtendedApolloService,
      deps: [ConfigService, Globals, HttpLink, Logger, TokenService, NgZone],
    },
  ],
})
export class AppModule {
  constructor(private readonly chunkLoadErrorMonitor: ChunkLoadErrorMonitorService) {}
}
